'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

export type PaginatedResponse<T> = {
  data: T[];
  total: number;
  hasMore: boolean;
};

export type PaginationState<T> = {
  data: T[];
  /**
   * `data` was produced by a PREVIOUS `loadData` — a changed query, sort or
   * filter whose replacement rows haven't landed yet.
   *
   * `isInitializing` cannot answer this. It is set from INSIDE the load effect,
   * so an effect running in the same commit still reads its pre-update value —
   * seeing the previous request's rows with nothing loading. This is derived
   * during render instead, so it is already true there. Anything DECIDING on
   * the rows (is this list empty?) has to consult it.
   */
  isStale: boolean;
  /**
   * The latest load FAILED. Anything deciding on the rows (is the list empty?
   * keep paging?) must stand down while it is set: after a failed first page
   * the rows are empty because nothing loaded, not because nothing exists, and
   * re-trying a failed page from an effect loops with no backoff. Cleared when
   * the next load starts.
   */
  hasError: boolean;
  isInitializing: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  total: number;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  replace: (keyword?: string) => Promise<void>;
  /** Directly update the data array (e.g. prepend a newly created item). */
  updateData: (updater: (data: T[]) => T[]) => void;
};

type LoadDataFn<T> = (
  page: number,
  pageSize: number,
  keyword?: string,
) => Promise<PaginatedResponse<T>>;

type Options = {
  pageSize?: number;
  autoLoad?: boolean;
};

/**
 * Generic async pagination hook.
 * Ported from PopshopRN's useAsyncPagination pattern.
 */
export function useAsyncPagination<T>(
  loadData: LoadDataFn<T>,
  options?: Options,
): PaginationState<T> {
  const pageSize = options?.pageSize ?? 25;
  const autoLoad = options?.autoLoad ?? true;

  const [data, setData] = useState<T[]>([]);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [hasError, setHasError] = useState(false);
  // Boxed so a function can live in state without `setState` reading it as an
  // updater. `null` until the first page lands — no data is stale data.
  const [loaded, setLoaded] = useState<{ fn: LoadDataFn<T> } | null>(null);

  const pageRef = useRef(1);
  const keywordRef = useRef<string | undefined>(undefined);
  const initIdRef = useRef(0);
  const isLoadingRef = useRef(false);

  const load = useCallback(
    async (page: number, keyword: string | undefined, initId: number) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;

      const isFirstPage = page === 1;

      setHasError(false);
      if (isFirstPage) {
        setIsInitializing(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const result = await loadData(page, pageSize, keyword);

        // Guard against stale responses after re-init
        if (initIdRef.current !== initId) return;

        setData(prev =>
          isFirstPage ? result.data : [...prev, ...result.data],
        );
        setLoaded({ fn: loadData });
        setTotal(result.total);
        setHasMore(result.hasMore);
        pageRef.current = page;
      } catch (error) {
        if (initIdRef.current !== initId) return;
        console.error('[useAsyncPagination] load error:', error);
        setHasError(true);
        // A failed first page still SETTLES this `loadData` — and must not
        // leave the PREVIOUS query's rows on screen as if they were this one's.
        // So the rows clear and `isStale` drops; `hasError` is what tells a
        // caller the empty list is a failure, not an empty library.
        if (isFirstPage) {
          setData([]);
          setTotal(0);
          setLoaded({ fn: loadData });
        }
      } finally {
        if (initIdRef.current === initId) {
          setIsInitializing(false);
          setIsLoadingMore(false);
          isLoadingRef.current = false;
        }
      }
    },
    [loadData, pageSize],
  );

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingRef.current) return;
    await load(pageRef.current + 1, keywordRef.current, initIdRef.current);
  }, [hasMore, load]);

  const refresh = useCallback(async () => {
    const newInitId = ++initIdRef.current;
    isLoadingRef.current = false;
    pageRef.current = 1;
    setData([]);
    await load(1, keywordRef.current, newInitId);
  }, [load]);

  const replace = useCallback(
    async (keyword?: string) => {
      keywordRef.current = keyword;
      const newInitId = ++initIdRef.current;
      isLoadingRef.current = false;
      pageRef.current = 1;
      setData([]);
      await load(1, keyword, newInitId);
    },
    [load],
  );

  // Auto-load on mount (and when load identity changes)
  const autoLoadRef = useRef(autoLoad);
  useEffect(() => {
    if (autoLoadRef.current) {
      const initId = ++initIdRef.current;
      isLoadingRef.current = false;
      // A new `loadData` starts over: if its first page fails, a later
      // `loadMore` must not page it from the previous query's position.
      pageRef.current = 1;
      void load(1, undefined, initId);
    }
  }, [load]);

  const updateData = useCallback((updater: (data: T[]) => T[]) => {
    setData(prev => updater(prev));
  }, []);

  return {
    data,
    isStale: loaded?.fn !== loadData,
    hasError,
    isInitializing,
    isLoadingMore,
    hasMore,
    total,
    loadMore,
    refresh,
    replace,
    updateData,
  };
}
